<template>
  <div class="container my-3">
    <div class="row">
      <div class="col-md-9">
        <CustomAlert type="danger" title=""
          ><span class="fs-4"
            >You are viewing lab data for
            <b
              >{{ fullname ? fullname : getFullname }} ({{
                onid ? onid : getOnid
              }})</b
            >
          </span></CustomAlert
        >
        <CustomAlert v-if="onid == ''" type="success" title=""
          ><span class="fs-4"
            >To submit this to Canvas, copy the following URL
            <input
              disabled
              class="form-control"
              :value="exportPath"
              id="copyText"
            /> </span
        ></CustomAlert>
        <CustomAlert type="secondary" title=""
          ><span class="h3 fw-bold"
            >Lab 2: Site Selection and Sampling</span
          ></CustomAlert
        >
        <form @submit.stop>
          <DiscussionQuestions
            :labContent="labContent"
            :discussionAnswers="formData.discussionAnswers"
            :exportMode="exportMode"
          />

          <!-- visitingSiteImages -->
          <DynamicCard title="Visiting your site">
            <div class="card-body">
              <div
                v-for="(
                  directions, index
                ) in labContent.visitingSitePhotoDirections"
                :key="index"
              >
                <div class="row">
                  <div class="col">
                    <p class="form-label text-start fw-bold m-3">
                      {{ index + 1 }}. {{ directions.text }}
                    </p>

                    <ul v-if="directions.stepsList">
                      <li
                        v-for="step in directions.stepsList"
                        :key="step"
                        class="lead text-start"
                      >
                        {{ step }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="index === 2">
                    <div
                      v-for="image in labContent.northSouthEastWestPhotoDirections"
                      :key="image.imageKey"
                    >
                      <SketchUpload
                        :sketchUploadHeaders="image"
                        :imageURL="findImageByKey(image.imageKey)"
                        :formId="labContent.id"
                        :exportMode="exportMode"
                      />
                    </div>
                  </div>
                  <div v-else>
                    <SketchUpload
                      :sketchUploadHeaders="directions"
                      :imageURL="findImageByKey(directions.imageKey)"
                      :formId="labContent.id"
                      :exportMode="exportMode"
                    />
                  </div>
                </div>
                <div
                  v-if="
                    index + 1 < labContent.visitingSitePhotoDirections.length
                  "
                  class="mb-5"
                >
                  <hr />
                </div>
              </div>
            </div>
          </DynamicCard>

          <DiscussionQuestions
            :labContent="labContent.discussionQuestionsClimate"
            :discussionAnswers="formData.discussionClimateAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsOrganisms.title"
            :labContent="labContent.discussionQuestionsOrganisms"
            :discussionAnswers="formData.discussionOrganismAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsTopology.title"
            :labContent="labContent.discussionQuestionsTopology"
            :discussionAnswers="formData.discussionTopologyAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsParentMaterial.title"
            :labContent="labContent.discussionQuestionsParentMaterial"
            :discussionAnswers="formData.discussionParentMaterialAnswers"
            :exportMode="exportMode"
          />

          <DiscussionQuestions
            :title="labContent.discussionQuestionsTime.title"
            :labContent="labContent.discussionQuestionsTime"
            :discussionAnswers="formData.discussionTimeAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsHumanActivity.title"
            :labContent="labContent.discussionQuestionsHumanActivity"
            :discussionAnswers="formData.discussionHumanActivityAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsSummary.title"
            :labContent="labContent.discussionQuestionsSummary"
            :discussionAnswers="formData.discussionSummaryAnswers"
            :exportMode="exportMode"
          />
        </form>
      </div>

      <div class="col-md-3">
        <LabSidebar
          :formData="formData"
          :exportMode="exportMode"
          routeName="ExportView"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";

import LabSidebar from "@/components/sidebar/LabSidebar.vue";

import labContent from "@/data/content.json";
import emptyForm from "@/data/formData.js";

import CustomAlert from "@/components/ui/CustomAlert";
import SketchUpload from "@/components/activity/SketchUpload";
import DynamicCard from "@/components/activity/DynamicCard";
import DiscussionQuestions from "@/components/activity/DiscussionQuestions";

export default {
  name: "ActivityView",
  components: {
    CustomAlert,
    LabSidebar,
    SketchUpload,
    DynamicCard,
    DiscussionQuestions
  },
  data() {
    return {
      labContent,
      formData: JSON.parse(JSON.stringify(emptyForm)),
      exportMode: true,
      fullname: ""
    };
  },
  props: {
    onid: {
      type: String,
      default: ""
    }
  },
  computed: {
    ...mapGetters(["getOnid", "getFullname", "getForm"]),
    ...mapGetters("datastore", ["getEntries", "getAdminEntries"]),
    ...mapState("datastore", ["entryLoaded", "entriesLoaded"]),
    exportPath() {
      let baseUrl = "";
      if (process.env.VUE_APP_DEBUG === "true") {
        baseUrl = "http://localhost:8080";
      } else {
        const protocol = "https://";
        const bucket = process.env.VUE_APP_DEPLOY_BUCKET;
        const path = process.env.VUE_APP_DEPLOY_PATH;
        baseUrl = `${protocol}${bucket}/${path}`;
      }
      let url = `${baseUrl}/#/export/${this.getOnid}/`;
      return url;
    },
    myOwnExport() {
      if (this.onid === this.getOnid) {
        return true;
      }
      return false;
    }
  },
  methods: {
    setWithDefault() {
      if (Object.keys(this.formData).length === 0) {
        this.formData = JSON.parse(JSON.stringify(emptyForm));
      }
    },
    findImageByKey(key) {
      return this.formData[key];
    },
    setData() {
      this.fullname = "";
      this.formData = {};
      const onidToCompare = this.onid === "" ? this.getOnid : this.onid;
      this.getAdminEntries.filter((entry) => {
        if (entry.onid === onidToCompare) {
          this.formData = { ...this.formData, ...entry?.value?.formData };
          this.fullname = entry.fullname;
        }
      });
      //* if the formData is still empty after looking for the adminEntries, set the form data to emptyForm as default
      this.setWithDefault();
    }
  },
  watch: {
    onid: function () {
      this.setData();
    }
  },
  mounted() {
    this.setData();
  }
};
</script>
